import { Auth } from '@aws-amplify/auth';
import { Amplify } from '@aws-amplify/core';
import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { cognitoConfiguration, getStore } from './ApplicationState';
import { dataResidencyManager, SupportedDataResidency } from './DataResidencyManager';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';
import { DataResidencyCache } from './SmartDataResidencyAuth';
import {
  CognitoUserProfileType,
  LoginState,
  expireSession,
  login,
  loginPartial,
  logout,
  reactivateSession,
  setUserProfile,
  startReactivation,
  updateField,
  UserType,
  LicenseLevel,
  CognitoUserProfileTypeAttributes,
} from './UserManagement';
import {
  getCleverOwnProfile,
  getCleverToken as gct,
  getUserProfile,
  resendNewPasswordConfirmation as rnpc,
  qrCodeSignInWithFallback as qrCodeSignInBackend,
} from './BackendInterface';
import { expireSessionAndRedirect } from './aaa';
import { logoutIntercomUser } from './IntercomUtilityFunctions';
import { unloadClassrooms } from './Classrooms';
import { getCleverProfile, getConfiguration, isTokenExpired, noWhiteSpace } from './GlobalFunctions';
import { setUser, unsetUser } from './error-tracker';
import { sendMixpanelReactivateEvent } from './mixpanel';
import { GetCleverTokenResponse, UserProfileType } from '../peekapak-types/DataProtocolTypes';
import { ErrorMessages } from '../peekapak-types/ErrorMessages';
declare let gapi: Record<string, any>;
type Provider = 'Google' | 'Clever' | 'ClassLink';
export type ConfirmationLinkState = 'EXPIRED' | 'OK' | 'STALE';

interface CleverProfile {
  data: {
    authorized_by: string;
    district: string;
    id: string;
    type: string;
  };
}

export function isElementaryStudent(profile: UserProfileType) {
  const { grade, type } = profile;
  const isStudent = type === 'Student';
  return isStudent && grade < 6;
}

export function getCleverToken(authorizationCode: string, redirectUri: string): Promise<GetCleverTokenResponse> {
  return new Promise((resolve, reject) => {
    gct(
      authorizationCode,
      redirectUri,
      (data) => {
        return resolve(data);
      },
      (error) => {
        return reject(error);
      },
    );
  });
}
export async function openHostedUIForGoogle() {
  return openHostedUI('Google');
}
export async function openHostedUIForClassLink() {
  return openHostedUI('ClassLink');
}
export async function openHostedUIForClever() {
  return openHostedUI('Clever');
}

// Function to set the flow type dynamically
function setAuthFlowType(flowType: string) {
  Amplify.configure({
    authenticationFlowType: flowType,
  });
}

async function setTokensManually(tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  username: string; // Add username as a parameter
}): Promise<CognitoUser> {
  const { idToken, accessToken, refreshToken, username } = tokens;
  try {
    // CRITICAL: Use Auth's internal userPool to ensure proper storage configuration
    // This ensures cookies are used if configured by initializeAmplifyForDataResidency
    // @ts-ignore - accessing private property for session sharing
    const cognitoUserPool = Auth.userPool;

    if (!cognitoUserPool) {
      throw new Error('Auth userPool not initialized. Ensure Amplify is configured.');
    }

    // Create a new CognitoUser object
    const cognitoUser = new CognitoUser({
      Username: username, // Use the username or identifier of the user
      Pool: cognitoUserPool,
    });

    // Create token objects using CognitoIdToken, CognitoAccessToken, and CognitoRefreshToken
    const idTokenObject = new CognitoIdToken({ IdToken: idToken });
    const accessTokenObject = new CognitoAccessToken({ AccessToken: accessToken });
    const refreshTokenObject = new CognitoRefreshToken({ RefreshToken: refreshToken });

    // Create a CognitoUserSession object
    const userSession = new CognitoUserSession({
      IdToken: idTokenObject,
      AccessToken: accessTokenObject,
      RefreshToken: refreshTokenObject,
    });

    // Set the session to the user object
    cognitoUser.setSignInUserSession(userSession);

    // CRITICAL: Store the tokens in the configured storage (cookies)
    // This makes the session available across apps on the same domain
    const storage = cognitoUserPool.storage;
    const clientId = cognitoUserPool.getClientId();
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

    console.log(`🔑 Storing tokens with keyPrefix: ${keyPrefix}, username: ${username}`);
    console.log(`🔑 Storage type:`, storage.constructor.name);

    storage.setItem(`${keyPrefix}.${username}.idToken`, idToken);
    storage.setItem(`${keyPrefix}.${username}.accessToken`, accessToken);
    storage.setItem(`${keyPrefix}.${username}.refreshToken`, refreshToken);
    storage.setItem(`${keyPrefix}.LastAuthUser`, username);

    // Also set the user in Auth's internal state
    // @ts-ignore - accessing private property
    Auth.user = cognitoUser;

    console.log('Tokens set manually. User session initialized and stored in configured storage.');

    return cognitoUser;
  } catch (error) {
    console.error('Error setting tokens manually:', error);
    throw error;
  }
}

export async function qrCodeSignIn(qrCode: string) {
  // any exceptions that occur in the backend will be thrown back to the caller

  // Get current data residency before backend call
  const currentDataResidency = dataResidencyManager.getCurrentDataResidency();
  console.log('🔐 QR code sign-in starting with data residency:', currentDataResidency);

  // Call backend - this might switch data residencies internally
  const response = await qrCodeSignInBackend(qrCode);

  // Check if data residency changed after backend call
  const postCallDataResidency = dataResidencyManager.getCurrentDataResidency();
  console.log('🔐 QR code sign-in backend returned, data residency is now:', postCallDataResidency);

  // CRITICAL: Initialize Amplify for the correct data residency BEFORE setting tokens
  // This ensures cookies are configured properly
  await initializeAmplifyForDataResidency(postCallDataResidency);

  // Store the data residency for future reactivations
  localStorage.setItem('peekapak.userDataResidency', postCallDataResidency);
  console.log('💾 Stored data residency for future reactivations:', postCallDataResidency);

  // Update ONLY the mandatorySignIn setting without overwriting cookie configuration
  Amplify.configure({
    Auth: {
      mandatorySignIn: false,
    },
  });

  const account = await setTokensManually(response);

  if (!account) {
    throw new Error('Error setting tokens manually');
  }

  const cognitoUser = account as unknown as CognitoUserProfileType;
  cognitoUser.username = account.getUsername();
  cognitoUser.attributes = await fetchUserAttributes(account);

  getStore().dispatch(login(cognitoUser));

  // need to wait for login to be in effect before using
  const profile = await getUserProfileFromDB();

  console.info(`${profile.email || profile.userId} sign in successful`);
  getStore().dispatch(setUserProfile(profile));
  setUser(profile.userId);

  // Cache the successful data residency for this user
  if (profile.email) {
    DataResidencyCache.cacheSuccessfulLogin(profile.email, postCallDataResidency);
  }

  return profile;

  async function fetchUserAttributes(cognitoUser: CognitoUser): Promise<CognitoUserProfileTypeAttributes> {
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((err: Error | undefined, attributes: CognitoUserAttribute[] | undefined) => {
        if (err) {
          console.error('Error fetching attributes:', err);
          reject(err);
        } else {
          // if there is no error, then attributes exists
          const attrMap = attributes!.reduce((acc: Record<string, string>, attr) => {
            acc[attr.getName()] = attr.getValue();
            return acc;
          }, {});
          resolve(attrMap as unknown as CognitoUserProfileTypeAttributes);
        }
      });
    });
  }
}

async function openHostedUI(provider: Provider) {
  try {
    // Check if OAuth is configured
    const authConfig = Auth.configure();

    if (!authConfig.oauth) {
      console.warn('⚠️ OAuth not configured, user requested OAuth login - switching to US data residency');

      // User is explicitly requesting OAuth, which means they must be a US user
      // (UAE doesn't support OAuth at all)
      const { dataResidencyManager, SupportedDataResidency } = await import('./DataResidencyManager');

      // Update stored data residency to US
      dataResidencyManager.setDataResidency(SupportedDataResidency.US);

      // Reinitialize Amplify with US configuration (which has OAuth)
      const { initializeAmplifyForDataResidency } = await import('./DataResidencyAwareAuthHelpers');
      await initializeAmplifyForDataResidency(SupportedDataResidency.US);

      // Refresh endpoints to match
      const { refreshEndpoints } = await import('./BackendInterface');
      refreshEndpoints();

      console.log('✅ Switched to US data residency for OAuth login');
    }

    // Now proceed with OAuth
    Auth.federatedSignIn({ provider });
  } catch (error) {
    console.error(`Failed to open hosted UI for ${provider}:`, error);
    throw error;
  }
}

export function resendConfirmationCode(username: string) {
  return new Promise((resolve, reject) => {
    Auth.resendSignUp(username)
      .then((data) => {
        return resolve(data);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}

export function checkConfirmationCode(username: string, code: string) {
  return new Promise((resolve, reject) => {
    Auth.confirmSignUp(username, code)
      .then((data) => {
        return resolve(data);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}
export async function reactivateUserSession() {
  try {
    await reactivateUserSessionIfExists();
  } catch (error) {
    if (!isInnocuousError(error)) {
      throw error;
    }
  }

  function isInnocuousError(error) {
    const message = (() => {
      if (error.message) return error.message;
      return error;
    })();

    switch (message) {
      case ErrorMessages.NO_ACCOUNT_FOUND_NOT_ROSTERED:
      case ErrorMessages.NO_ACCOUNT_FOUND_INCOMPLETE:
      case ErrorMessages.NO_ACCOUNT_FOUND_TEACHER_STUDENT_MIXUP:
      case 'The user is not authenticated':
      case 'No current user':
        return true;
      default:
        return false;
    }
  }
}

/** obtain the user's Cognito User Profile with a fresh session token
 */
export async function getRefreshedCognitoUser(): Promise<CognitoUserProfileType> {
  const session = await Auth.currentSession();

  // console.debug(
  //   `%cReactivating -  got current session`,
  //   'background: black; color: yellow'
  // );

  const cognitoUser = await Auth.currentAuthenticatedUser();

  cognitoUser.refreshSession(session.getRefreshToken(), () => {
    // console.debug(
    //   '%cRefreshed auth session',
    //   'background: black; color: yellow'
    // );
  });

  // console.debug(
  //   `%cReactivating - got cognito user, user session expires ${getReadableUnixTimeAnsi(
  //     expiryFromUser * 1000
  //   )} / ${expiryFromUser}, from session = ${expiryFromSession}`,
  //   'background: black; color: yellow'
  // );

  return cognitoUser;
}

/* eslint-disable complexity */
export async function reactivateUserSessionIfExists(): Promise<void> {
  const currentLoggedInState = getStore().getState().user.loginState;

  console.log(
    '🔍 reactivateUserSessionIfExists called with state:',
    currentLoggedInState,
    'at',
    new Date().toISOString(),
  );

  if (currentLoggedInState === LoginState.storybookAccess) {
    console.log('↩️ Returning early: storybookAccess');
    // console.debug(`%cParent storybook access detected`);
    return;
  }

  if (currentLoggedInState === LoginState.loggedIn) {
    const expiry = getStore().getState().user.cognitoProfile.signInUserSession.idToken.payload.exp;

    if (!isTokenExpired(expiry)) {
      console.log('↩️ Returning early: token not expired, expiry:', new Date(expiry * 1000).toISOString());
      return;
    }

    console.log('⏰ Token is expired, time since expiry:', Math.floor(Date.now() / 1000) - expiry, 'seconds');
    // console.debug(
    //   `%cToken is expired ...`,
    //   'background: blue; color: yellow',
    //   `time to expiry = `,
    //   Math.floor(Date.now() / 1000) - expiry
    // );
  }

  console.log('📊 Current login state before checking reactivating:', currentLoggedInState);
  console.log('📊 LoginState.reactivating value:', LoginState.reactivating);
  console.log('📊 LoginState.authenticated value:', LoginState.authenticated);

  // console.debug(
  //   `%cReactivating - no longer logged in or too close to token expiry -> `,
  //   'background: black; color: yellow',
  //   currentLoggedInState
  // );

  if (currentLoggedInState === LoginState.reactivating || currentLoggedInState === LoginState.authenticated) {
    console.log('⏳ State is', currentLoggedInState, '- subscribing to wait for state change...');
    // console.debug(
    //   `%cReactivating - login state is reactivating, subscribing`,
    //   'background: black; color: yellow'
    // );
    return new Promise((resolve) => {
      console.log('🔔 Setting up subscription to Redux store');
      const unsubscribe = getStore().subscribe(() => {
        const observedState = getStore().getState().user.loginState;
        console.log(
          '👀 Store subscription fired - observed state:',
          observedState,
          'vs current:',
          currentLoggedInState,
        );

        if (observedState !== currentLoggedInState) {
          console.log('✅ State changed from', currentLoggedInState, 'to', observedState, '- resolving Promise');
          unsubscribe();
          return resolve();
        }
      });
      console.log('🔔 Subscription set up, waiting for state change from:', currentLoggedInState);
    });
  }

  console.log('🚀 Proceeding to line 314 - dispatching startReactivation()');
  getStore().dispatch(startReactivation());

  // console.debug(
  //   `%cReactivating has started`,
  //   'background: black; color: yellow'
  // );

  // Ensure we're using the correct data residency before attempting to refresh session
  try {
    const storedDataResidency = localStorage.getItem('peekapak.userDataResidency') as SupportedDataResidency;
    if (storedDataResidency && Object.values(SupportedDataResidency).includes(storedDataResidency)) {
      console.log('🌍 Ensuring correct data residency for reactivation:', storedDataResidency);
      await initializeAmplifyForDataResidency(storedDataResidency);
    } else {
      console.log('⚠️ No stored data residency found, using current configuration');
    }
  } catch (error) {
    console.warn('⚠️ Could not initialize data residency for reactivation:', error);
    // Continue anyway - maybe the default region is correct
  }

  console.log('🔄 Starting try block - attempting to get refreshed Cognito user');
  try {
    const cognitoUser = await getRefreshedCognitoUser();
    console.log('✅ Got refreshed Cognito user:', cognitoUser?.getUsername());

    if (nativeAccountAlreadyExists(cognitoUser)) {
      console.log('❌ Native account already exists - throwing error');
      throw new Error(`User Pool account already exists`);
    }

    console.log('📤 Dispatching reactivateSession with Cognito user');
    getStore().dispatch(reactivateSession(cognitoUser));

    let peekapakUserProfile;
    console.log('📥 Attempting to get user profile from DB');
    try {
      peekapakUserProfile = await getUserProfileFromDB();
      console.log('✅ Got user profile:', peekapakUserProfile?.userId, peekapakUserProfile?.email);
    } catch (getUserProfileError) {
      console.log('❌ Failed to get user profile, dispatching loginPartial:', getUserProfileError);
      getStore().dispatch(loginPartial());
      throw getUserProfileError;
    }

    // console.debug(
    //   `%cReactivating - got user profile ${peekapakUserProfile.userId}`,
    //   'background: black; color: yellow'
    // );
    console.log('📤 Dispatching setUserProfile and calling setUser');
    getStore().dispatch(setUserProfile(peekapakUserProfile));
    setUser(peekapakUserProfile.userId);

    const identities = cognitoUser.signInUserSession.idToken.payload.identities;
    console.log('🔍 Checking for external identities:', identities?.length || 0, 'identities found');

    if (identities) {
      // console.debug(
      //   `%cReactivating - figuring out external identities`,
      //   'background: black; color: yellow'
      // );

      const googleIdentity = identities.filter((identity) => {
        return identity.providerName === 'Google';
      });

      if (googleIdentity.length > 0) {
        const googleUserId = googleIdentity[0].userId;
        // console.debug( `User has Google identity: ${ googleUserId }` );
        await initializeGoogleApiClients(googleUserId);
        getStore().dispatch(
          updateField({
            field: 'googleAccountUserId',
            value: googleUserId,
          }),
        );
      }

      const cleverIdentity = identities.filter((identity) => {
        return identity.providerName === 'Clever';
      });

      if (cleverIdentity.length > 0) {
        const cleverAccessToken = cognitoUser?.attributes?.['custom:pmAccessToken'];

        if (cleverAccessToken) {
          const cleverAccountUserId = cognitoUser?.attributes?.['custom:pmUserId'];

          if (!cleverAccountUserId) throw new Error(`Expected to find Clever User ID in account but did not`);

          try {
            const cleverProfile = (await getCleverProfile(cleverAccountUserId, cleverAccessToken)) as Record<
              string,
              unknown
            >;

            updateCleverCredentials({ data: { id: cleverAccountUserId } }, cleverAccessToken, { data: cleverProfile });
          } catch {
            // could not get clever profile so probably the access token has
            // expired
          }
        }
      }
    }

    // console.debug(
    //   `%cReactivating - figuring out Clever access`,
    //   'background: black; color: yellow'
    // );
    console.info(`✅ SUCCESS: User ${peekapakUserProfile.email || peekapakUserProfile.userId} session reactivated`);
    console.log('📊 Final state after reactivation:', getStore().getState().user.loginState);
    sendMixpanelReactivateEvent(peekapakUserProfile);
  } catch (err) {
    console.log('❌ CAUGHT ERROR in reactivation:', err);
    if (!(err instanceof Error) && typeof err !== 'string') {
      console.log('🔄 Re-throwing non-Error/non-string error');
      throw err;
    }

    const errorMessage = (err as Error)?.message || (err as string);
    console.log('🔍 Error message for switch statement:', errorMessage);

    switch (errorMessage) {
      case 'No current user':
        getStore().dispatch(logout());
        break;

      case 'Refresh Token has expired':
        expireSessionAndRedirect(); // will dispatch expire session

        break;

      case 'Network error':
        console.warn(noWhiteSpace`Network error prevented reactivate user: ${errorMessage}, \
            possibly logged out. Current loginState === ${getStore().getState().user.loginState}`);
        break;

      default:
        console.warn(noWhiteSpace`Cannot reactivate user: ${errorMessage}, \
            possibly logged out. Current loginState === ${getStore().getState().user.loginState}`);
        break;
    }

    throw err;
  }

  function nativeAccountAlreadyExists(data) {
    const { payload } = data.signInUserSession.idToken;
    return payload.isUserPoolAccountAlreadyExists;
  }
}

export async function getUserProfileFromDB(): Promise<UserProfileType> {
  const result = await getUserProfile();
  return result;
}

export function resendNewPasswordConfirmation(username: string): Promise<void> {
  return new Promise((resolve, reject) => {
    rnpc(
      username,
      (_) => {
        return resolve();
      },
      (error) => {
        return reject(error);
      },
    );
  });
}
export function forceChangePassword(
  username: string,
  temporaryPassword: string,
  newPassword: string,
): Promise<{
  email: string;
}> {
  return new Promise((resolve, reject) => {
    Auth.signIn(username, temporaryPassword)
      .then((user) => {
        if (!user.challengeName || user.challengeName !== 'NEW_PASSWORD_REQUIRED') {
          return reject(new Error(`Did not receive expected new password required challenge`));
        }

        return Auth.completeNewPassword(user, newPassword, user.challengeParam.requiredAttributes);
      })
      .then((user) => {
        getStore().dispatch({
          type: 'LOGIN',
          payload: user,
        });
        return getUserProfileFromDB();
      })
      .then((userProfile) => {
        getStore().dispatch({
          type: 'SET_USER_PROFILE',
          payload: userProfile,
        });
        console.info(`Force change password succeeded for ${userProfile.email}`);
        setUser(userProfile.userId);
        return resolve(userProfile);
      })
      .catch((error) => {
        return reject(error);
      });
  });
}
export function getNewPasswordConfirmationLinkState(
  username: string,
  temporaryPassword: string,
): Promise<ConfirmationLinkState> {
  return new Promise((resolve, reject) => {
    Auth.signIn(username, temporaryPassword)
      .then((user) => {
        if (!user.challengeName || user.challengeName !== 'NEW_PASSWORD_REQUIRED') {
          return reject(new Error(`Did not receive expected new password required challenge`));
        }

        return resolve('OK');
      })
      .catch((error) => {
        if (error.message.includes('Incorrect username or password')) {
          return resolve('STALE');
        } else if (error.message.includes('User account has expired, it must be reset by an administrator.')) {
          return resolve('EXPIRED');
        } else if (error.message.includes('Temporary password has expired')) {
          return resolve('EXPIRED');
        }

        return reject(error);
      });
  });
}
export async function signInUser(email: string, password: string): Promise<UserProfileType> {
  // Use USER_PASSWORD_AUTH flow
  setAuthFlowType('USER_PASSWORD_AUTH');

  // Use intelligent multi-data-residency sign in
  const { intelligentDataResidencySignIn } = await import('./SmartDataResidencyAuth');
  const result = await intelligentDataResidencySignIn(email, password);

  getStore().dispatch(login(result.user));
  const profile = await getUserProfileFromDB();

  console.info(`${profile.email || profile.userId} sign in successful`);
  getStore().dispatch(setUserProfile(profile));
  setUser(profile.userId);
  return profile;
}

export async function userChangePassword(oldPassword: string, newPassword: string) {
  const currentUser = await Auth.currentAuthenticatedUser();
  await Auth.changePassword(currentUser, oldPassword, newPassword);
  return true;
}
export async function initiateForgotPasswordFlow(username: string) {
  const data = await Auth.forgotPassword(username);
  return data;
}
export async function forgotPasswordSubmit(username: string, verificationCode: string, newPassword: string) {
  const data = await Auth.forgotPasswordSubmit(username, verificationCode, newPassword);
  return data;
}
export async function signOutUser() {
  try {
    const loggedInUser = getStore().getState().user;
    const currentCognitoUser = await Auth.currentAuthenticatedUser();

    if (loggedInUser && loggedInUser.cognitoProfile) {
      console.info(`Logging out ${loggedInUser.cognitoProfile.username}...`);

      signOutFromLocalState();
      signOutOfCognito();
      return;
    } else if (currentCognitoUser) {
      signOutOfCognito();
      return;
    }
  } catch (error) {
    if (!isInnocuousError(error as Error)) {
      throw error;
    }

    return;
  }

  function isInnocuousError(error: Error) {
    const message = (() => {
      if (error.message) return error.message;
      return error;
    })();

    switch (message) {
      case ErrorMessages.NO_ACCOUNT_FOUND_NOT_ROSTERED:
      case ErrorMessages.NO_ACCOUNT_FOUND_INCOMPLETE:
      case ErrorMessages.NO_ACCOUNT_FOUND_TEACHER_STUDENT_MIXUP:
      case 'The user is not authenticated':
      case 'No current user':
        return true;
      default:
        return false;
    }
  }

  function signOutFromLocalState() {
    getStore().dispatch(logout());
    getStore().dispatch(unloadClassrooms());
    logoutIntercomUser();
    unsetUser();
  }

  function signOutOfCognito(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          return resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
}
export function isUserParentAccessStorybook(): boolean {
  return getStore().getState().user.loginState === LoginState.storybookAccess;
}
export function isUserDistrictAdministrator(): boolean {
  const userProfile = getStore().getState().user?.userProfile;
  if (!userProfile) return false;

  return (
    userProfile.userType === UserType.districtAdmin || userProfile.licenseLevel === LicenseLevel.districtAdministrator
  );
}

export function isUserLoggedIn(): boolean {
  return getStore().getState().user.loginState === LoginState.loggedIn;
}
export function isUserLoggedInStateUnknown(): boolean {
  const currentState = getStore().getState().user.loginState;
  return currentState === LoginState.unknown;
}
export function isUserLoggedInStateInTransition(): boolean {
  const currentState = getStore().getState().user.loginState;
  return (
    currentState === LoginState.unknown ||
    currentState === LoginState.reactivating ||
    currentState === LoginState.authenticated
  );
}
export function isTokenUnavailable(): boolean {
  const currentState = getStore().getState().user.loginState;
  // console.debug(`isTokenUnavailable currentState = `, currentState);
  return currentState === LoginState.unknown || currentState === LoginState.reactivating;
}
export function waitUntilLoginState(timeout = 10000, isReadyFunction: (arg0: string) => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    const intervalLength = 500;
    let intervalCount = 0;
    const intervalId = setInterval(checkState, intervalLength);

    function checkState() {
      const currentState = getStore().getState().user.loginState;
      // console.debug(
      //   `%ccurrentState `,
      //   'background: blue; color: yellow',
      //   currentState
      // );

      if (isReadyFunction.call(null, currentState)) {
        clearInterval(intervalId);
        return resolve();
      }

      intervalCount += 1;

      if (intervalLength * intervalCount > timeout) {
        clearInterval(intervalId);
        return reject(Error(`waitUntilUserLoginState Timeout expired`));
      }
    }
  });
}
export async function waitUntilTokenIsAvailable(timeout = 10000): Promise<void> {
  return await waitUntilLoginState(timeout, (currentState) => {
    return currentState !== LoginState.unknown && currentState !== LoginState.reactivating;
  });
}
export async function waitUntilUserLoggedInStateKnown(timeout = 20000): Promise<void> {
  return await waitUntilLoginState(timeout, (currentState) => {
    return (
      currentState !== LoginState.unknown &&
      currentState !== LoginState.authenticated &&
      currentState !== LoginState.reactivating
    );
  });
}
export function getRefreshTokenExpiry() {
  const initialAuthTime = getStore().getState().user.cognitoProfile.getSignInUserSession().getIdToken()
    .payload.auth_time;

  // the refresh token expiry is hardcoded and set in
  // the Cognito user pool app client configuration
  return initialAuthTime + 60 * 60 * 24;
}

export function isUserSessionExpired(): boolean {
  if (isUserLoggedIn()) {
    const refreshTokenExpiry = getRefreshTokenExpiry() + 60 * 60 * 24;
    const expired = isTokenExpired(getRefreshTokenExpiry());
    if (expired) {
      getStore().dispatch(expireSession());
      return true;
    }

    return false;
  }

  console.warn(`User is not logged in but session expiry is being checked. This could be a logical error.`);
  return false;
}

export function initializeGoogleApiClients(googleUserId = ''): Promise<void> {
  return new Promise((resolve, reject) => {
    if (gapi && gapi.auth2 && gapi.client.classroom) {
      resolve();
    }

    const auth2Params = {
      client_id: getConfiguration('google').clientId,
      immediate: true,
      login_hint: '',
    };
    const driveClientParams = {
      discoveryDocs: getConfiguration('google').discoveryDocs,
      immediate: true,
      login_hint: '',
    };

    if (googleUserId) {
      auth2Params.login_hint = googleUserId;
      driveClientParams.login_hint = googleUserId;
    } else {
      delete auth2Params.login_hint;
      delete driveClientParams.login_hint;
    }

    gapi.load('client:auth2', {
      callback: () => {
        // console.debug( 'GAPI: client auth api loaded' );

        if (!gapi.auth2.getAuthInstance()) {
          gapi.auth2
            .init(auth2Params)
            .then(() => {
              // console.debug( 'GAPI: auth2 init done' );
              gapi.client
                .init(driveClientParams)
                .then(() => {
                  // console.debug( 'GAPI: classroom client init done' );
                  resolve();
                })
                .catch((error: Error) => {
                  if (error.message === 'idpiframe_initialization_failed') {
                    console.error('oops init failed');
                  }
                  reject(error);
                });
            })
            .catch((error: Error) => {
              if (error.message === 'idpiframe_initialization_failed') {
                console.error('oops init failed');
              }
              reject(error);
            });
        } else {
          gapi.client
            .init(driveClientParams)
            .then(() => {
              // console.debug( 'GAPI: classroom client init done' );
              resolve();
            })
            .catch((error: Error) => {
              if (error.message === 'idpiframe_initialization_failed') {
                console.error('oops init failed');
              }
              reject(error);
            });
        }
      },
      onerror: (error: Error) => {
        if (error.message === 'idpiframe_initialization_failed') {
          console.error('oops init failed');
        }
        reject(error);
      },
    });
  });
}
export function isGoogleUserSignedIn(): boolean {
  if (gapi && gapi.auth2) {
    return gapi.auth2.getAuthInstance().isSignedIn.get();
  }

  return false;
}
export async function isCleverUserSignedIn(): Promise<boolean> {
  const currentLoggedInState = getStore().getState().user.loginState;

  if (currentLoggedInState === LoginState.loggedIn) {
    const userProfile = getStore().getState().user.userProfile;
    if (userProfile) {
      // NB2: The below NB may not be true.....
      // NB: we do not need to check the expiry; the Clever access token would
      // have been obtained when the user logged in using Clever, so if the
      // user's Cognito refresh token is still valid, then the Clever access
      // token will be as well, since they both expire 24 hours after grant. If,
      // on the other hand, the user logged in using Cognito and then later
      // obtained a Clever access token by clicking on the + Sync using Clever
      // button, then the Clever access token will not expire before the Cognito
      // one does, which will prompt the user to re-login
      if (userProfile?.cleverAccessToken && userProfile.cleverAccessToken.exp > Date.now()) {
        return true;
      }

      // userProfile exists but no clever access token
      const cognitoProfile = getStore().getState().user.cognitoProfile;
      if (cognitoProfile?.attributes['custom:pmAccessToken']) {
        const at = cognitoProfile.attributes['custom:pmAccessToken'];

        try {
          const cleverProfile = (await getCleverOwnProfile(at)) as CleverProfile;
          updateCleverCredentials(cleverProfile, at, cleverProfile, true);

          return true;
        } catch {
          return false;
        }
      }
    }

    return false;
  }

  return false;
}
export function getSignedInGoogleUserId(): string {
  if (gapi && gapi.auth2 && isGoogleUserSignedIn()) {
    return gapi.auth2.getAuthInstance().currentUser.get().getId();
  }

  throw new Error(`Google user not signed in`);
}
export async function promptUserGoogleSignIn(): Promise<void> {
  const { scope } = getConfiguration('google');

  if (gapi && gapi.auth2) {
    await gapi.auth2.getAuthInstance().signIn({
      prompt: 'select_account',
      scope,
    });
  } else {
    throw new Error(`Google API clients not initialized`);
  }
}
export async function signOutGoogleAccount() {
  if (gapi && gapi.auth2) {
    await gapi.auth2.getAuthInstance().signOut();
  } else {
    throw new Error(`Google API clients not initialized`);
  }
}
export function isGoogleApiInitialized() {
  const signedIn = isSignedIn();
  const classroomApiAccessible = !!gapi.client && !!gapi.client.classroom;
  return signedIn && classroomApiAccessible;

  function isSignedIn() {
    return gapi && gapi.auth2 && gapi.auth2.getAuthInstance() && gapi.auth2.getAuthInstance().isSignedIn.get();
  }
}

function storeCleverCredentialsInProfile(
  cleverAccountUserId: string,
  cleverAccessToken: Record<string, unknown>,
  cleverProfile: Record<string, unknown>,
) {
  getStore().dispatch(updateField({ field: 'cleverAccountUserId', value: cleverAccountUserId }));
  getStore().dispatch(updateField({ field: 'cleverAccessToken', value: cleverAccessToken }));
  getStore().dispatch(updateField({ field: 'cleverProfile', value: cleverProfile }));
}

export function updateCleverCredentials(
  cleverId: {
    data: {
      id: string;
    };
  },
  cleverAccessToken: string,
  profile: {
    data: Record<string, unknown>;
  },
  isExpiryUnknown?: boolean,
) {
  const cleverAccountUserId = cleverId.data.id;
  const cleverProfile = profile.data;
  const cleverAccessTokenWrapper = {
    token: cleverAccessToken,
    // NB: if we don't know the expiry, set the expiry to 10 minutes from the
    // present moment
    expiry: isExpiryUnknown ? Date.now() + 600 * 1000 : Date.now() + 24 * 3600 * 1000,
  };
  storeCleverCredentialsInProfile(cleverAccountUserId, cleverAccessTokenWrapper, cleverProfile);
}

export function isStudent(userProfile: UserProfileType | null) {
  return userProfile?.type === 'Student';
}
